// Where to send the user after login when the auth guard bounced them from a
// dashboard URL (e.g. an email deep link to /dashboard/not-activated?activation=…).
// Carried as /auth/login?redirect=…, and also remembered in localStorage for
// the magic-link flow, whose callback URL can't carry it.

const STORAGE_KEY = 'loginReturnTo'
const MAX_AGE_MS = 60 * 60 * 1000

// Only same-origin dashboard paths: rejects absolute URLs, protocol-relative
// (//host) and backslash tricks, which all fail the prefix or origin check.
export function safeReturnTo(value: string | null | undefined): string | null {
    if (!value || !value.startsWith('/dashboard')) return null
    try {
        const url = new URL(value, window.location.origin)
        if (url.origin !== window.location.origin) return null
        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        return null
    }
}

export function loginPathFor(returnTo: string): string {
    const safe = safeReturnTo(returnTo)
    return safe && safe !== '/dashboard' && safe !== '/dashboard/'
        ? `/auth/login?redirect=${encodeURIComponent(safe)}`
        : '/auth/login'
}

export function rememberReturnTo(returnTo: string | null) {
    if (!returnTo) return
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({path: returnTo, at: Date.now()}))
    } catch {
        // Storage unavailable (private mode): the ?redirect= param still works.
    }
}

// The explicit param wins; otherwise a fresh remembered one. Always clears it.
export function consumeReturnTo(fromQuery: string | null): string | null {
    let stored: string | null = null
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        window.localStorage.removeItem(STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) as {path?: string; at?: number} : null
        if (parsed?.path && typeof parsed.at === 'number' && Date.now() - parsed.at < MAX_AGE_MS) stored = parsed.path
    } catch {
        stored = null
    }
    return safeReturnTo(fromQuery) ?? safeReturnTo(stored)
}
